import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoginRoute } from "./routes/auth/login";
import { XCallbackRoute } from "./routes/auth/callback/x";
import { EmailCallbackRoute } from "./routes/auth/callback/email";
import { ClaimRoute } from "./routes/auth/claim";
import { DashboardRoute } from "./routes/app/index";
import { FamViewerRoute } from "./routes/app/fam/[slug]";
import { ProposeIdeaRoute } from "./routes/app/fam/[slug]/propose";
import { IdeaDetailRoute } from "./routes/app/fam/[slug]/idea/[id]";
import { FamSettingsRoute } from "./routes/app/fam/[slug]/settings";
import { FantasyIndexRoute } from "./routes/app/fantasy/index";
import { FantasyLeagueRoute } from "./routes/app/fantasy/[id]";
import { WeeklyLeaderboardRoute } from "./routes/app/fantasy/weekly";
import { QuarterlyLeaderboardRoute } from "./routes/app/fantasy/quarterly";
import { EarningsLeaderboardRoute } from "./routes/app/fantasy/earnings";
import { DuelsListRoute } from "./routes/app/fantasy/duels";
import { DuelDetailRoute } from "./routes/duels/[id]";
import { FantasyFamDetailRoute } from "./routes/app/fantasy-fam/[slug]";
import { FantasyFamJoinRoute } from "./routes/app/fantasy-fam/join";
import { PowerRankingRoute } from "./routes/app/power-ranking";
import { SettingsRoute } from "./routes/app/settings";
import { UpgradeRoute } from "./routes/app/upgrade";
import { BillingSettingsRoute } from "./routes/app/settings/billing";
import { AdminRoute } from "./routes/app/admin";
import { StockDetailRoute } from "./routes/stock/[symbol]";
import { AuthGuard } from "./components/AuthGuard";

// Configure TanStack Query with sane defaults. All server-state in the app
// goes through this client; we never reach for fetch-in-useEffect.
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1,
			refetchOnWindowFocus: false,
			staleTime: 30_000,
		},
	},
});

export default function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<Routes>
					{/* Auth — public */}
					<Route path="/auth/login" element={<LoginRoute />} />
					<Route path="/auth/callback/x" element={<XCallbackRoute />} />
					<Route path="/auth/callback/email" element={<EmailCallbackRoute />} />

					{/* App — guarded, mounted at root */}
					<Route
						path="/"
						element={
							<AuthGuard>
								<DashboardRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fam/:slug"
						element={
							<AuthGuard>
								<FamViewerRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fam/:slug/propose"
						element={
							<AuthGuard>
								<ProposeIdeaRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fam/:slug/idea/:id"
						element={
							<AuthGuard>
								<IdeaDetailRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fam/:slug/settings"
						element={
							<AuthGuard>
								<FamSettingsRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fantasy"
						element={
							<AuthGuard>
								<FantasyIndexRoute />
							</AuthGuard>
						}
					/>
					{/* Fantasy Fams umbrella — public leaderboards (weekly/quarterly/
					    earnings) and the authed Duels list. Duel detail is mounted at
					    /duels/:id (public spectator). The legacy /fantasy/:id route
					    for themed weekly leagues stays below; the new sub-paths are
					    siblings, not children, so ordering matters — list the literal
					    paths BEFORE the catch-all :id parameter route. */}
					<Route path="/fantasy/weekly" element={<WeeklyLeaderboardRoute />} />
					<Route
						path="/fantasy/quarterly"
						element={<QuarterlyLeaderboardRoute />}
					/>
					<Route
						path="/fantasy/earnings"
						element={<EarningsLeaderboardRoute />}
					/>
					<Route
						path="/fantasy/duels"
						element={
							<AuthGuard>
								<DuelsListRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/fantasy/:id"
						element={
							<AuthGuard>
								<FantasyLeagueRoute />
							</AuthGuard>
						}
					/>
					{/* Duel spectator page — public so tweet links land without auth. */}
					<Route path="/duels/:id" element={<DuelDetailRoute />} />

					{/* Fantasy Fam entity detail (migration 041) */}
					<Route
						path="/fantasy-fam/:slug"
						element={
							<AuthGuard>
								<FantasyFamDetailRoute />
							</AuthGuard>
						}
					/>
					{/* Shared invite-link landing — redeems the token, then
					    redirects into the Fantasy Fam. AuthGuard bounces
					    unauthenticated visitors through login first. */}
					<Route
						path="/fantasy-fam/join/:token"
						element={
							<AuthGuard>
								<FantasyFamJoinRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/power-ranking"
						element={
							<AuthGuard>
								<PowerRankingRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/settings"
						element={
							<AuthGuard>
								<SettingsRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/settings/billing"
						element={
							<AuthGuard>
								<BillingSettingsRoute />
							</AuthGuard>
						}
					/>
					<Route
						path="/upgrade"
						element={
							<AuthGuard>
								<UpgradeRoute />
							</AuthGuard>
						}
					/>

					{/* Stock detail — standalone, not scoped to a Fam. Linked to from
					    Fam pages, the dashboard, and future explore surfaces. */}
					<Route
						path="/stock/:symbol"
						element={
							<AuthGuard>
								<StockDetailRoute />
							</AuthGuard>
						}
					/>

					{/* Founder operational dashboard — server-side gated by
					    FAM env.FOUNDER_USER_ID. Non-founders see a "not
					    authorized" stub on this route. No navbar entry. */}
					<Route
						path="/admin"
						element={
							<AuthGuard>
								<AdminRoute />
							</AuthGuard>
						}
					/>

					{/* Claim a fam invite — authed. AuthGuard kicks unauthenticated
					    visitors back to /auth/login?next=/claim?token=… so the SPA
					    redeems immediately after sign-in. */}
					<Route
						path="/claim"
						element={
							<AuthGuard>
								<ClaimRoute />
							</AuthGuard>
						}
					/>

					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</BrowserRouter>
		</QueryClientProvider>
	);
}
